import { z } from 'zod';

/**
 * Wire contracts for the no-code Approval workflow (Phase 4.5 →
 * Phase 8 server-side swap).
 *
 * Two endpoints' worth of payload, each split into list / upsert /
 * delete:
 *
 *   - `/api/approvers`           — project-scoped approver registry.
 *   - `/api/approval-requests`   — project + flow-scoped requests.
 *
 * Shapes mirror `packages/flow-builder/src/lib/approval/types.ts`
 * verbatim and are stored as JSONB so a frontend evolution (adding
 * a metadata key, recording a new decision flavour) doesn't need a
 * backend re-roll. The blob is `passthrough()`-ed for forward
 * compatibility — the API treats unknown keys as a feature, not a
 * bug.
 */

// ---- Approver ---------------------------------------------------------

export const ApproverPayloadSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    email: z.string().optional(),
    role: z.string().optional(),
  })
  .passthrough();
export type ApproverPayload = z.infer<typeof ApproverPayloadSchema>;

export const ApproverListResponseSchema = z.object({
  items: z.array(ApproverPayloadSchema),
});
export type ApproverListResponse = z.infer<typeof ApproverListResponseSchema>;

export const ApproverUpsertRequestSchema = ApproverPayloadSchema;
export type ApproverUpsertRequest = z.infer<typeof ApproverUpsertRequestSchema>;

// ---- Approval request ------------------------------------------------

export const ApprovalActionPayloadSchema = z
  .object({
    approverId: z.string().min(1),
    approverName: z.string().min(1),
    decision: z.enum(['approved', 'rejected']),
    reason: z.string().optional(),
    decidedAt: z.string(),
  })
  .passthrough();
export type ApprovalActionPayload = z.infer<typeof ApprovalActionPayloadSchema>;

export const ApprovalRequestStateSchema = z.enum(['requested', 'approved', 'rejected', 'cancelled']);
export type ApprovalRequestState = z.infer<typeof ApprovalRequestStateSchema>;

/**
 * Session 6 of the P1 head-to-head roadmap — request kind taxonomy.
 *
 * Two values today:
 *
 *   - `run-gate` (default, back-compat) — the original Phase 8 use:
 *     a flow targets a protected environment and needs sign-off
 *     before the toolbar Run button will dispatch. Consulted by
 *     `evaluateRunGate`.
 *   - `test-plan-signoff` — Session 6's new use: a manual sign-off
 *     ledger entry that records "Alice signed off v3 of this flow
 *     for the Release Candidate cut" without participating in the
 *     run-gate logic. The flow card surfaces the latest approved
 *     row as a pill so reviewers can see the sign-off state at a
 *     glance.
 *
 * Older API builds that haven't shipped this schema bump will not
 * send the `kind` field; the Zod default keeps validation green
 * and treats the omitted value as `run-gate`. New clients should
 * set it explicitly so the email fan-out and the flow-card pill
 * can vary per kind.
 *
 * Adding a new kind in the future is purely additive — extend this
 * union + the corresponding switch in:
 *   - `lib/approval/operations.ts` (run-gate filter)
 *   - `services/approval-notifications-service.ts` (email copy)
 *   - frontend ApprovalPanel kind selector
 *   - flow-card pill renderer
 */
export const ApprovalRequestKindSchema = z.enum(['run-gate', 'test-plan-signoff']);
export type ApprovalRequestKind = z.infer<typeof ApprovalRequestKindSchema>;

export const ApprovalRequestPayloadSchema = z
  .object({
    id: z.string().min(1),
    projectId: z.string().min(1),
    flowId: z.string().min(1),
    title: z.string().min(1),
    description: z.string().optional(),
    requestedApproverIds: z.array(z.string()).default([]),
    requestedBy: z.string().min(1),
    requestedAt: z.string(),
    state: ApprovalRequestStateSchema,
    actions: z.array(ApprovalActionPayloadSchema).default([]),
    metadata: z.record(z.string()).optional(),
    /**
     * Session 6 — discriminator between Phase 8's run-gate approvals
     * and the new test-plan sign-off ledger entries. Defaults to
     * `run-gate` so omitted fields on the wire (older clients,
     * existing DB rows pre-dating this schema) keep the Phase 8
     * behaviour.
     */
    kind: ApprovalRequestKindSchema.default('run-gate'),
  })
  .passthrough();
export type ApprovalRequestPayload = z.infer<typeof ApprovalRequestPayloadSchema>;

export const ApprovalRequestListResponseSchema = z.object({
  items: z.array(ApprovalRequestPayloadSchema),
});
export type ApprovalRequestListResponse = z.infer<typeof ApprovalRequestListResponseSchema>;

export const ApprovalRequestUpsertRequestSchema = ApprovalRequestPayloadSchema;
export type ApprovalRequestUpsertRequest = z.infer<typeof ApprovalRequestUpsertRequestSchema>;

// ---- Shared delete envelope (re-used by both approver + request) ------

export const ApprovalDeleteResponseSchema = z.object({
  deleted: z.boolean(),
});
export type ApprovalDeleteResponse = z.infer<typeof ApprovalDeleteResponseSchema>;
